import { AppSuccess } from '../../models/AppSuccess';
import { Project_Base } from '../../models/Project';
import { User_Base } from '../../models/User';
import { AC_Description } from './AC_Type';

// Access control configuration
export enum AC_Project_Role {
  PROJECT_ADMIN = 'PROJECT_ADMIN',
  PROJECT_EDITOR = 'PROJECT_EDITOR',
  PROJECT_COLLECTOR = 'PROJECT_COLLECTOR',
  PROJECT_MEMBER = 'PROJECT_MEMBER',
  MEMBER_CHUOI_LIEN_KET = 'MEMBER_CHUOI_LIEN_KET',
}

export enum AC_Project_Permission {
  MANAGE_PROJECT = 'MANAGE_PROJECT',
  MANAGE_USER = 'MANAGE_USER',
  MANAGE_BLOG = 'MANAGE_BLOG',
  MANAGE_COLLECT_DATA = 'MANAGE_COLLECT_DATA',
  MANAGE_PORTAL_SIDEBAR = 'MANAGE_PORTAL_SIDEBAR',
  MANAGE_SHOP = 'MANAGE_SHOP',
  COLLECT_DATA = 'COLLECT_DATA',
}

export const AC_Project_RolePermission = {
  PROJECT_ADMIN: [
    'MANAGE_PROJECT',
    'MANAGE_USER',
    'MANAGE_BLOG',
    'MANAGE_COLLECT_DATA',
    'MANAGE_PORTAL_SIDEBAR',
    'MANAGE_SHOP',
    'COLLECT_DATA',
  ],
  PROJECT_EDITOR: [
    'MANAGE_PROJECT',
    'MANAGE_BLOG',
    'MANAGE_COLLECT_DATA',
    'MANAGE_PORTAL_SIDEBAR',
    'MANAGE_SHOP',
    'COLLECT_DATA',
  ],
  PROJECT_COLLECTOR: ['COLLECT_DATA'],
  PROJECT_MEMBER: [],
  MEMBER_CHUOI_LIEN_KET: [],
};

// API types
export type AC_Project_getMyRoles_Request_Query = {
  projectId: number;
};
export type AC_Project_getMyRoles_Response = Array<AC_Project_Role>;

///
export type AC_Project_getUsers_with_Roles_Request_Query = {
  projectId: number;
  userId?: number;
};

export type AC_Project__User_with_Roles = {
  user: User_Base;
  roles: Array<AC_Project_Role>;
};
export type AC_Project_getUsers_with_Roles_Response = Array<AC_Project__User_with_Roles>;
///

export type AC_Project_getMyPermissions_Request_Query = {
  projectId: number;
};
export type AC_Project_getMyPermissions_Response = Array<AC_Project_Permission>;

export type AC_Project_Base = {
  id?: number;
  user?: User_Base;
  project?: Project_Base;
  role: AC_Project_Role;
};
export type AC_Project_addRoleForUser_Request = {
  userId: number;
  projectId: number;
  role: AC_Project_Role;
};
export type AC_Project_addRoleForUser_Response = AppSuccess<AC_Project_Base>;

export type AC_Project_deleteRoleForUser_Request_Query = {
  userId: number;
  projectId: number;
  role: AC_Project_Role;
};
export type AC_Project_deleteRoleForUser_Response = AppSuccess<AC_Project_Base>;

export type AC_Project_getPublicUsers_Request_Query = {
  role?: AC_Project_Role;
  projectId: number;
};

export type AC_Project_getPublicUsers_Response = Array<User_Base>;

export type AC_Project_getPublicUserDetail_Request_Query = {
  userId?: number;
  role?: AC_Project_Role;
  projectId: number;
};

export type AC_Project_getPublicUserDetail_Response = User_Base;

export type I18nDescription = {
  en: string;
  vi: string;
};
const getRoleName = (role: AC_Project_Role): I18nDescription => {
  switch (role) {
    case AC_Project_Role.PROJECT_ADMIN:
      return { en: 'Project', vi: 'Quản trị viên' };
    case AC_Project_Role.PROJECT_EDITOR:
      return { en: 'Project Editor', vi: 'Biên tập viên' };
    case AC_Project_Role.PROJECT_COLLECTOR:
      return { en: 'Project Collector', vi: 'Người thu thập' };
    case AC_Project_Role.MEMBER_CHUOI_LIEN_KET:
      return { en: 'Chuỗi liên kết', vi: 'Thành viên chuỗi liên kết' };
    default:
      return { en: 'Unknown role', vi: 'Role không đúng' };
  }
};
// utils
const getRoleDescription = (role: AC_Project_Role): AC_Description => {
  switch (role) {
    case AC_Project_Role.PROJECT_ADMIN:
      return { en: 'full access in the project', vi: 'toàn quyền đổi với dự án' };
    case AC_Project_Role.PROJECT_EDITOR:
      return { en: 'full access, but cannot manage user', vi: 'toàn quyền, nhưng không thể quản lý người dùng' };
    case AC_Project_Role.PROJECT_COLLECTOR:
      return { en: 'can only collect data', vi: 'thu thập dữ liệu' };
    case AC_Project_Role.MEMBER_CHUOI_LIEN_KET:
      return { en: 'a chuoi-lien-ket normal user - no special role in this project', vi: 'chuỗi liên kết' };
    default:
      return { en: 'unknown role', vi: 'role không đúng' };
  }
};

const getPermissionDescription = (permission: AC_Project_Permission): AC_Description => {
  switch (permission) {
    case AC_Project_Permission.MANAGE_PROJECT:
      return { en: 'Manage project', vi: 'Quản lý dự án' };
    case AC_Project_Permission.MANAGE_USER:
      return { en: 'Manage user', vi: 'Quản lý người dùng' };
    case AC_Project_Permission.MANAGE_BLOG:
      return { en: 'Manage blog', vi: 'Quản lý blog' };
    case AC_Project_Permission.MANAGE_COLLECT_DATA:
      return { en: 'Manage collect data', vi: 'Quản lý dữ liệu thu thập' };
    case AC_Project_Permission.COLLECT_DATA:
      return { en: 'Collect data', vi: 'Thu thập dữ liệu' };
    case AC_Project_Permission.MANAGE_PORTAL_SIDEBAR:
      return { en: 'Manage portal sidebar', vi: 'Quản lý cổng thông tin' };
    case AC_Project_Permission.MANAGE_SHOP:
      return { en: 'Manage shop', vi: 'Quản lý cửa hàng' };
    default:
      return { en: 'Unknown permission', vi: 'Quyền không hợp lệ' };
  }
};

const getPermissions = (roles: Array<AC_Project_Role>): Array<AC_Project_Permission> => {
  let permissionsSet = new Set<AC_Project_Permission>();

  for (let i = 0; i < roles.length; i++) {
    let role = roles[i];
    let permissions = AC_Project_RolePermission[role];
    permissions.forEach((permission) => {
      permissionsSet.add(permission as AC_Project_Permission);
    });
  }

  return Array.from(permissionsSet);
};

export const AC_Project_ModelUtil = {
  getRoleDescription,
  getPermissionDescription,
  getDescription: getPermissionDescription,
  getPermissions,
  getRoleName,
};

export const AC_Project = AC_Project_ModelUtil;

